import { Maybe } from 'pratica';
import _get from 'lodash/get';
import _toLower from 'lodash/toLower';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import _keys from 'lodash/keys';
import _sumBy from 'lodash/sumBy';
import _reduce from 'lodash/reduce';

import { ORDERED_CUSTOMIZATION_TYPES } from 'cards/constants/CardTypes';
import { actionTypes } from './actions';
import { pageName } from '../../util';
import { isCustomizationActive } from '../../util/isCustomizationActive';
import { findLeadCustomization, getTypeFromCustomization } from '../../util/customization';


const init = {
  review: {
    summary: null,
    notes: [],
    total: {
      printed: 0,
      blank: 0,
      all: 0
    }
  },
  busy: true,
  hasPendingChanges: false,
};

const Mapper = (project, meta) => Maybe({ project, meta })
  .map((data) => {
    let mappedCustomizations = [];
    const { project: projectData, meta: metaData } = data;
    const { customizations } = projectData;
    const leadCustomization = findLeadCustomization(customizations);

    if (leadCustomization) {
      const { type: leadCustomizationType } = leadCustomization;
      const leadCardType = getTypeFromCustomization(leadCustomization);
      const projectMetaFromType = _get(metaData, _toLower(leadCustomizationType));
      const shouldSplitProject = (projectMetaFromType
        && projectMetaFromType.printed > 0
        && projectMetaFromType.blanks + projectMetaFromType.printed === leadCustomization.quantity);
      if (shouldSplitProject) {
        // Set temporary blank customization object, from leadCustomization
        const blankCardsCustomization = Object.assign({}, leadCustomization, {
          name: `${leadCustomization.name} - Blank`,
          quantity: projectMetaFromType.blanks,
        });

        // Set temporary printed customization object from leadCustomization
        const leadCardPrice = _get(leadCustomization, 'variation.price_cents') || 0;
        const printedCardsCustomization = Object.assign({}, leadCustomization, {
          name: `${leadCustomization.name} - Printed Message`,
          quantity: projectMetaFromType.printed,
          variation: {
            ...leadCustomization.variation,
            price_cents: (projectMetaFromType.printed_extra_price_cents + leadCardPrice),
            'option_values': {
              ...leadCustomization.variation.option_values,
              // Inside printed messages do not have foil
              foil: 'none',
              'foil-color': 'none',
            }
          },
          urlSlug: pageName(`${leadCardType}_INSIDE`),
          split: shouldSplitProject,
        });

        // Removed splitted customization and add the rest
        const remainderCustomizations = _filter(customizations, customization => customization.uuid !== leadCustomization.uuid);
        mappedCustomizations = [...remainderCustomizations, blankCardsCustomization, printedCardsCustomization];
      } else {
        _keys(customizations).map(uuid => mappedCustomizations.push(customizations[uuid]));
      }

      // All data sorted out, splited vs unsplited customizations
      return mappedCustomizations.reduce((result, customization) => {
        if (customization && customization.variation) {
          const { variation } = customization;

          const cardType = getTypeFromCustomization(customization);
          const urlSlug = pageName(cardType);

          // make sure not to include any deleted customizations in order view
          // getProjectOrderView is used in subtotal selector so it's important to filter out deleted
          // customizations to avoid messing up subtotal
          if (variation && isCustomizationActive(customization)) {
            const orderView = {
              customizationUUID: customization.uuid,
              name: customization.name,
              type: customization.type,
              quantity: customization.quantity,
              recipientAddressing: customization.recipient_addressing,
              urlSlug: customization.urlSlug || urlSlug,
              priceCents: variation.price_cents,
              imageUrls: variation.image_urls,
              color: variation.option_values.color,
              size: variation.option_values.size,
              paperType: variation.option_values['paper-type'] || null,
              liningColor: variation.option_values['envelope-lining'] || 'none',
              split: customization.split || false,
              foilColor: variation.option_values['foil-color'],
            };
            result.push(orderView);
          }
        }
        return _sortBy(result, o => ORDERED_CUSTOMIZATION_TYPES.indexOf(o.type));
      }, []);
    }
    return null;
  })
  .cata({
    Just: mappedSummary => mappedSummary,
    Nothing: () => state // eslint-disable-line
  });

// Actions


// Reducer
const reducer = (state = init, action) => {
  switch (action.type) {
    case actionTypes.REVIEW_SUMMARY_FETCH_FULFILLED:
      return Maybe(action.payload)
        .map((data) => {
          const [project, meta] = data;
          const mappedSummary = Mapper(project, meta);
          const priceSummary = _reduce(mappedSummary, (result, item) => {
            if (!item.split && item.urlSlug === 'detailInside') {
              Object.assign(result, { blank: (item.priceCents * item.quantity) || 0 });
            } else if (item.split && item.urlSlug === 'detailInside') {
              Object.assign(result, { printed: (item.priceCents * item.quantity) || 0 });
            }
            return result;
          }, {});

          return {
            ...state,
            review: {
              summary: mappedSummary,
              total: {
                ...priceSummary,
                all: _sumBy(mappedSummary, cust => (cust.priceCents * cust.quantity) || 0)
              }
            }
          };
        })
        .cata({
          Just: updatedState => updatedState,
          Nothing: () => state
        });
    case actionTypes.CUSTOM_NOTES_FETCH_FULFILLED:
      return Maybe(action.payload)
        .map((notes) => {
          const projectNotes = _reduce(notes, (result, item) => Object.assign(result, { [item.uuid]: item.data }), {});
          return {
            ...state,
            busy: false,
            review: {
              ...state.review,
              notes: {
                ...projectNotes
              }
            }
          };
        })
        .cata({
          Just: updatedState => updatedState,
          Nothing: () => state
        });
    case actionTypes.CUSTOM_NOTES_FETCH_REJECTED:
      return {
        ...state,
        busy: false
      };
    case actionTypes.SET_PENDING_CHANGES:
      return {
        ...state,
        hasPendingChanges: action.payload
      };
    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/cards/reducers/customization/reducer.js